import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/axios';

// Agent Center · projekt-unabhängige Library: Templates + Skills.
// Alle Calls mit skipProject:true, weil die Library nichts mit dem
// aktuellen Projekt zu tun hat (Projekt-Bindung kommt in eigener Layer).

export function useAgentTemplates(filters = {}) {
  return useQuery({
    queryKey: ['agent-templates', filters],
    queryFn: async () => {
      const params = {};
      if (filters.kind) params.kind = filters.kind;
      if (filters.active !== undefined) params.active = filters.active;
      const { data } = await api.get('/agent-templates', { params, skipProject: true });
      return data;
    },
  });
}

export function useAgentTemplate(id) {
  return useQuery({
    queryKey: ['agent-template', id],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get(`/agent-templates/${id}`, { skipProject: true });
      return data.template;
    },
  });
}

export function useSaveAgentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const cfg = { skipProject: true };
      const { data } = id
        ? await api.patch(`/agent-templates/${id}`, payload, cfg)
        : await api.post('/agent-templates', payload, cfg);
      return data.template;
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['agent-templates'] });
      if (tpl?.id) qc.invalidateQueries({ queryKey: ['agent-template', tpl.id] });
    },
  });
}

export function useDeleteAgentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/agent-templates/${id}`, { skipProject: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-templates'] }),
  });
}

export function useAgentSkills(filters = {}) {
  return useQuery({
    queryKey: ['agent-skills', filters],
    queryFn: async () => {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.active !== undefined) params.active = filters.active;
      const { data } = await api.get('/agent-skills', { params, skipProject: true });
      return data;
    },
  });
}

export function useSaveAgentSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const cfg = { skipProject: true };
      const { data } = id
        ? await api.patch(`/agent-skills/${id}`, payload, cfg)
        : await api.post('/agent-skills', payload, cfg);
      return data.skill;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-skills'] }),
  });
}

export function useDeleteAgentSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/agent-skills/${id}`, { skipProject: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-skills'] }),
  });
}

// Projekt-Agents (deterministische Slot-Struktur pro Projekt) ───────────────
// projectId-Param NICHT skipProject — Axios fügt automatisch ?project=… an.

export function useProjectAgents(projectId) {
  return useQuery({
    queryKey: ['project-agents', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await api.get('/project-agents');
      return data;
    },
  });
}

export function useAssignProjectAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => api.post('/project-agents', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-agents'] }),
  });
}

export function useRemoveProjectAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/project-agents/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-agents'] }),
  });
}

// Agent-OS · Board (Status + Deps + letzter Lauf) und Intent-Schnittstelle ────
// Kein skipProject: Axios hängt das aktuelle Projekt automatisch an.

export function useAgentBoard(projectId) {
  return useQuery({
    queryKey: ['agent-board', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await api.get('/agent-board');
      return data;
    },
  });
}

export function useAgentIntent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ intent, params }) => {
      const { data } = await api.post('/agent-intent', { intent, params });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agent-board'] }),
  });
}
